import { useEffect, useState } from "react";
import { getAdminOrders, getAdminContacts, deleteAdminOrders, formatEUR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Lock, RefreshCw, LogOut, LayoutDashboard, ShoppingBag, MessageSquare, Tag, Smartphone, Settings, Trash2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { AdminPlans } from "@/components/admin/AdminPlans";
import { AdminPhones } from "@/components/admin/AdminPhones";
import { AdminSettings } from "@/components/admin/AdminSettings";

const TOKEN_KEY = "ct-admin-token";

const statusColor = (s) => {
  if (s === "pagamento_dichiarato") return "bg-amber-500 text-white";
  if (s === "evaso") return "bg-emerald-600 text-white";
  return "bg-secondary text-secondary-foreground";
};

const statusLabel = (s) => {
  if (s === "in_attesa_pagamento") return "In attesa";
  if (s === "pagamento_dichiarato") return "Pagamento dichiarato";
  if (s === "evaso") return "Evaso";
  return s;
};

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggleOrder = (id) =>
    setSelectedOrders((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAllOrders = () =>
    setSelectedOrders((prev) => (prev.length === orders.length ? [] : orders.map((o) => o.id)));

  const doDeleteOrders = async () => {
    setDeleting(true);
    try {
      const res = await deleteAdminOrders(token, selectedOrders);
      toast.success(`${res.deleted ?? selectedOrders.length} ordine/i eliminato/i.`);
      setConfirmDelete(false);
      setSelectedOrders([]);
      await load(token, { silent: true });
    } catch (e) {
      toast.error("Errore nell'eliminazione degli ordini.");
    } finally {
      setDeleting(false);
    }
  };

  const load = async (t, { silent } = {}) => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([getAdminOrders(t), getAdminContacts(t)]);
      setOrders(o);
      setContacts(c);
      setSelectedOrders([]);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      return true;
    } catch (e) {
      if (!silent) toast.error("Token non valido o errore di accesso.");
      localStorage.removeItem(TOKEN_KEY);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Ripristino sessione admin da localStorage
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) {
      setToken(saved);
      load(saved, { silent: true }).finally(() => setBooting(false));
    } else {
      setBooting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setToken("");
    setOrders([]);
    setContacts([]);
  };

  if (!authed) {
    return (
      <div data-testid={ADMIN.page} className="container-x py-24">
        <Card className="mx-auto max-w-sm p-8 rounded-2xl border-border bg-card glow">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-accent glow"><Lock className="h-6 w-6" /></span>
          <h1 className="mt-4 text-xl font-semibold text-center">Area riservata</h1>
          <p className="mt-1 text-sm text-muted-foreground text-center">Inserisci il token amministratore per accedere.</p>
          <form onSubmit={(e) => { e.preventDefault(); load(token); }} className="mt-6 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="token">Token</Label>
              <Input id="token" type="password" data-testid={ADMIN.tokenInput} value={token} onChange={(e) => setToken(e.target.value)} placeholder="••••••••" className="rounded-xl" disabled={booting} />
            </div>
            <Button type="submit" disabled={loading || booting} data-testid={ADMIN.login} className="w-full rounded-xl glow-hover">{loading ? "Accesso..." : booting ? "Verifica sessione..." : "Accedi"}</Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid={ADMIN.page} className="container-x py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Pannello Amministratore</h1>
          <p className="text-sm text-muted-foreground">controllatelefoni.it — gestione completa del sito</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => load(token)} data-testid={ADMIN.refresh} className="rounded-xl"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
          <Button variant="ghost" onClick={logout} data-testid={ADMIN.logout} className="rounded-xl text-muted-foreground"><LogOut className="h-4 w-4" /> Esci</Button>
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="mt-6">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 rounded-xl p-1">
          <TabsTrigger value="dashboard" data-testid={ADMIN.analyticsTab} className="rounded-lg gap-1.5"><LayoutDashboard className="h-4 w-4" /> Dashboard</TabsTrigger>
          <TabsTrigger value="orders" data-testid={ADMIN.ordersTab} className="rounded-lg gap-1.5"><ShoppingBag className="h-4 w-4" /> Ordini ({orders.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid={ADMIN.contactsTab} className="rounded-lg gap-1.5"><MessageSquare className="h-4 w-4" /> Contatti ({contacts.length})</TabsTrigger>
          <TabsTrigger value="prices" data-testid={ADMIN.pricesTab} className="rounded-lg gap-1.5"><Tag className="h-4 w-4" /> Piani</TabsTrigger>
          <TabsTrigger value="phones" data-testid={ADMIN.phonesTab} className="rounded-lg gap-1.5"><Smartphone className="h-4 w-4" /> Telefoni</TabsTrigger>
          <TabsTrigger value="settings" data-testid={ADMIN.settingsTab} className="rounded-lg gap-1.5"><Settings className="h-4 w-4" /> Impostazioni</TabsTrigger>
        </TabsList>

        {/* Dashboard / Analytics */}
        <TabsContent value="dashboard" className="mt-6">
          <AdminDashboard token={token} />
        </TabsContent>

        {/* Ordini */}
        <TabsContent value="orders" className="mt-6">
          {selectedOrders.length > 0 && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3">
              <p className="text-sm font-medium">{selectedOrders.length} ordine/i selezionato/i</p>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                data-testid={ADMIN.deleteOrders}
                className="rounded-xl"
              >
                <Trash2 className="h-4 w-4" /> Elimina selezionati
              </Button>
            </div>
          )}
          <Card className="rounded-2xl border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid={ADMIN.selectAllOrders}
                      checked={orders.length > 0 && selectedOrders.length === orders.length}
                      onCheckedChange={toggleAllOrders}
                      aria-label="Seleziona tutti gli ordini"
                    />
                  </TableHead>
                  <TableHead>Rif.</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Articoli</TableHead>
                  <TableHead>Totale</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} data-state={selectedOrders.includes(o.id) ? "selected" : undefined}>
                    <TableCell>
                      <Checkbox
                        data-testid={`${ADMIN.orderCheckbox}-${o.id}`}
                        checked={selectedOrders.includes(o.id)}
                        onCheckedChange={() => toggleOrder(o.id)}
                        aria-label={`Seleziona ordine ${o.id.slice(0, 8)}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell>{o.customer?.full_name}</TableCell>
                    <TableCell className="text-xs">{o.customer?.email}</TableCell>
                    <TableCell className="text-xs max-w-[220px]">{o.items?.map((i) => i.name).join(", ")}</TableCell>
                    <TableCell className="font-semibold">{formatEUR(o.total)}</TableCell>
                    <TableCell><Badge className={`rounded-md ${statusColor(o.status)}`}>{statusLabel(o.status)}</Badge></TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessun ordine</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>

          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent className="rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Confermi la cancellazione?</AlertDialogTitle>
                <AlertDialogDescription>
                  Stai per eliminare <strong>{selectedOrders.length}</strong> ordine/i selezionato/i. L'azione non può essere annullata.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={doDeleteOrders}
                  disabled={deleting}
                  data-testid={ADMIN.confirmDeleteOrders}
                  className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleting ? "Eliminazione..." : "Sì, elimina"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* Contatti */}
        <TabsContent value="contacts" className="mt-6">
          <Card className="rounded-2xl border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead>Messaggio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell className="text-xs">{c.email}</TableCell>
                    <TableCell className="text-xs">{c.phone || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[320px]">{c.message}</TableCell>
                  </TableRow>
                ))}
                {contacts.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nessun contatto</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Piani / Prezzi */}
        <TabsContent value="prices" className="mt-6">
          <AdminPlans token={token} />
        </TabsContent>

        {/* Telefoni */}
        <TabsContent value="phones" className="mt-6">
          <AdminPhones token={token} />
        </TabsContent>

        {/* Impostazioni Google */}
        <TabsContent value="settings" className="mt-6">
          <AdminSettings token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
